'use-client';
import * as React from 'react';
import type { MouseEventHandler } from 'react';
import { useRef, useCallback, useMemo } from 'react';
import classNames from 'clsx';
import {
  activateBySpaceOrEnterButton,
  activateByEnterButton,
  getQaDataAttributes,
  getDataAttributes,
  getAccessibilityAttributesSpread,
} from '@wix/editor-elements-common-utils';
import { useAnalyticsReportClicks } from '@wix/editor-elements-common-utils/hooks';
import { isValidLink } from '@wix/thunderbolt-elements/components/Link';
import type {
  IStylableButtonProps,
  IStylableButtonEventHandlers,
  IStylableButtonImperativeActions,
} from '../../../StylableButton.types';
import { IconAnimationTriggers } from '../../../StylableButton.types';
import { TestIds } from '../../../constants';
import stylableButtonSemanticClassNames from '../../../StylableButton.semanticClassNames';
import { addPrefixToId, createIconFromString } from '../../utils';
import type { AnimatedSvgRef } from '../../../../AnimatedSvg/AnimatedSvg.types';
import AnimatedSvg from '../../../../AnimatedSvg/viewer/AnimatedSvg';

export interface ButtonProps extends IStylableButtonProps {
  rootClassName: string;
  labelClassName: string;
  iconClassName: string;
  linkClassName?: string;
  BuilderAnimationIcon?: React.ComponentType<any>;
}

const getEventHandlers = (
  {
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
  }: Partial<IStylableButtonEventHandlers>,
  isLink: boolean,
  isDisabled: boolean,
) => {
  return {
    onClick,
    onMouseEnter,
    onMouseLeave,
    onKeyDown: isLink ? activateByEnterButton : activateBySpaceOrEnterButton,
    onDoubleClick: !isDisabled && onDblClick ? onDblClick : undefined,
    onFocus: !isDisabled && onFocus ? onFocus : undefined,
    onBlur: !isDisabled && onBlur ? onBlur : undefined,
  };
};

const Button: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  ButtonProps
> = (props, ref) => {
  const {
    id,
    link,
    type = 'button',
    svgString,
    isIconAnimated = false,
    svgAnimatedIcon,
    label,
    isDisabled,
    isQaMode,
    fullNameCompType,
    reportBiOnClick,
    a11y,
    corvid,
    onDblClick,
    onMouseEnter: propsOnMouseEnter,
    onMouseLeave: propsOnMouseLeave,
    onFocus,
    onBlur,
    ariaAttributes,
    onClick: propsOnClick,
    preventLinkNavigation,
    compPreviewState,
    reducedMotion,
    lang,
    direction,
    rootClassName,
    labelClassName,
    iconClassName,
    linkClassName,
    BuilderAnimationIcon,
  } = props;

  const isLink = isValidLink(link);
  const shouldPreventLinkNavigation = preventLinkNavigation && isLink;
  const shouldHaveOnClick =
    (!isDisabled && propsOnClick) || shouldPreventLinkNavigation;

  const {
    iconSvgString,
    iconCollapsed,
    iconAnimationTriggers = [IconAnimationTriggers.HOVER],
  } = corvid || {};

  const buttonOrLinkRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(
    null,
  );
  const overrideIconRef = useRef(false);
  const iconAnimatedRef = useRef<AnimatedSvgRef>(null);

  React.useImperativeHandle(ref, () => {
    return {
      focus: () => {
        buttonOrLinkRef.current?.focus();
      },
      blur: () => {
        buttonOrLinkRef.current?.blur();
      },
      animateIconForward: () => {
        onAnimationForward();
      },
      animateIconBackward: () => {
        onAnimationBackward();
      },
    };
  });

  const onAnimationForward = useCallback(() => {
    !isDisabled &&
      (isIconAnimated || BuilderAnimationIcon) &&
      iconAnimatedRef.current?.runAnimationForward();
  }, [isDisabled, isIconAnimated, BuilderAnimationIcon]);

  const onAnimationBackward = useCallback(() => {
    !isDisabled &&
      (isIconAnimated || BuilderAnimationIcon) &&
      iconAnimatedRef.current?.runAnimationBackward();
  }, [isDisabled, isIconAnimated, BuilderAnimationIcon]);

  const a11yAttr = React.useMemo(
    () =>
      getAccessibilityAttributesSpread({
        ariaAttributes: {
          ...ariaAttributes,
          ...a11y,
          label: ariaAttributes?.label ?? (a11y.label || label),
        },
        tabindex: a11y?.tabindex as 0 | -1,
      }),
    [a11y, label, ariaAttributes],
  );

  const onMouseEnter = useCallback<MouseEventHandler>(
    (event: React.MouseEvent) => {
      iconAnimationTriggers.includes(IconAnimationTriggers.HOVER) &&
        onAnimationForward();
      propsOnMouseEnter?.(event);
    },
    [iconAnimationTriggers, onAnimationForward, propsOnMouseEnter],
  );

  const onMouseLeave = useCallback<MouseEventHandler>(
    (event: React.MouseEvent) => {
      iconAnimationTriggers.includes(IconAnimationTriggers.HOVER) &&
        onAnimationBackward();
      propsOnMouseLeave?.(event);
    },
    [iconAnimationTriggers, onAnimationBackward, propsOnMouseLeave],
  );

  const onClick = useAnalyticsReportClicks({
    reportBiOnClick,
    onClick: shouldHaveOnClick
      ? (event: React.MouseEvent) => {
          shouldPreventLinkNavigation && event.preventDefault();
          !isDisabled && propsOnClick?.(event);
        }
      : undefined,
  });

  const eventHandlers = React.useMemo(
    () =>
      getEventHandlers(
        {
          onClick,
          onDblClick,
          onMouseLeave,
          onMouseEnter,
          onFocus,
          onBlur,
        },
        isLink,
        isDisabled,
      ),
    [
      onClick,
      onDblClick,
      onMouseLeave,
      onMouseEnter,
      onFocus,
      onBlur,
      isLink,
      isDisabled,
    ],
  );

  const semanticClassNames =
    props.semanticClassNames || stylableButtonSemanticClassNames;

  const wrapperAttributes = {
    id,
    ...getDataAttributes(props),
    ...getQaDataAttributes(isQaMode, fullNameCompType),
    'data-semantic-classname': semanticClassNames.root,
    lang,
    ...(direction && { dir: direction }),
  };

  // Build icon (reusing existing logic from StylableButtonCommon)
  overrideIconRef.current = false;
  const buildIconStatic = useMemo(() => {
    if (!iconCollapsed && iconSvgString !== null) {
      if (iconSvgString) {
        overrideIconRef.current = true;
        return createIconFromString(addPrefixToId(iconSvgString, id));
      } else if (svgString) {
        return createIconFromString(addPrefixToId(svgString, id));
      }
    }
    return null;
  }, [iconCollapsed, iconSvgString, id, svgString]);

  const buildIconAnimated = useMemo(
    () =>
      isIconAnimated && svgAnimatedIcon ? (
        <AnimatedSvg
          svgContent={svgAnimatedIcon}
          reducedMotion={reducedMotion}
          ref={iconAnimatedRef}
        />
      ) : null,
    [isIconAnimated, reducedMotion, svgAnimatedIcon],
  );

  const legacyButtonIcon = useMemo(() => {
    return isIconAnimated ? buildIconAnimated : buildIconStatic;
  }, [isIconAnimated, buildIconAnimated, buildIconStatic]);

  const renderButtonContent = () => (
    <>
      {label && (
        <span className={labelClassName} data-testid={TestIds.buttonLabel}>
          {label}
        </span>
      )}
      {BuilderAnimationIcon ? (
        <BuilderAnimationIcon
          ref={iconAnimatedRef}
          className={iconClassName}
          data-testid={TestIds.builderAnimationIcon}
        />
      ) : (
        legacyButtonIcon && (
          <span
            className={iconClassName}
            aria-hidden="true"
            data-testid={TestIds.buttonIcon}
          >
            {legacyButtonIcon}
          </span>
        )
      )}
    </>
  );

  const renderButton = () => (
    <button
      type={type}
      disabled={isDisabled}
      className={rootClassName}
      data-testid={TestIds.buttonContent}
      {...wrapperAttributes}
      {...a11yAttr}
      {...eventHandlers}
      ref={buttonOrLinkRef as React.RefObject<HTMLButtonElement>}
      data-preview={compPreviewState}
    >
      {renderButtonContent()}
    </button>
  );

  const renderLinkedButton = () => {
    const linkHref = isDisabled ? undefined : link!.href;
    const linkRel = link!.rel;
    const linkTarget = link!.target;
    const linkPopupId = link!.linkPopupId;
    const anchorDataId = link!.anchorDataId;
    const anchorCompId = link!.anchorCompId;

    return (
      <a
        href={linkHref}
        rel={linkRel}
        target={linkTarget}
        data-popupid={linkPopupId}
        data-anchor={anchorDataId}
        data-anchor-comp-id={anchorCompId}
        className={classNames(linkClassName, rootClassName)}
        data-testid={TestIds.buttonContent}
        {...wrapperAttributes}
        {...a11yAttr}
        {...eventHandlers}
        role={a11yAttr.role}
        tabIndex={a11yAttr.tabIndex}
        ref={buttonOrLinkRef as React.RefObject<HTMLAnchorElement>}
        data-preview={compPreviewState}
      >
        {renderButtonContent()}
      </a>
    );
  };

  const renderDisabledLinkAsButton = () => (
    <button
      type={type}
      disabled={isDisabled}
      className={classNames(rootClassName)}
      data-testid={TestIds.buttonContent}
      {...wrapperAttributes}
      {...a11yAttr}
      {...eventHandlers}
      role="link"
      ref={buttonOrLinkRef as React.RefObject<HTMLButtonElement>}
      data-preview={compPreviewState}
    >
      {renderButtonContent()}
    </button>
  );

  if (isDisabled && isLink) {
    return renderDisabledLinkAsButton();
  }
  return isLink ? renderLinkedButton() : renderButton();
};

export default React.forwardRef(Button);
